/**
 * ES Chart Application
 *
 * Main application component: the ES candlestick chart with the
 * greeks/levels indicator stack (lightweight-charts).
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { ESChart, CacheStatusBar, NavigationBar, IndicatorSidePanel } from './components';
import { useLiveCandles, useLevelIndicators } from './hooks';
import { useLiveVolumeCandles } from './hooks/useLiveVolumeCandles';
import CONFIG from './config';
import { authFetch } from './auth/authFetch';
import { getLatestSessionDate } from './utils/market';
import { OVERLAY_STATE_IDS, OVERLAY_TOGGLE_DEFAULTS, buildQuoteOverlays } from './indicators/underlyingOverlays';
import { useAuth } from './auth/AuthContext';
import AdminPanel from './admin/AdminPanel';
import DocsPanel from './docs/DocsPanel';

// getETTime / getLatestSessionDate are imported from ./utils/market (shared SSOT).

const INDICATOR_DEFAULTS = {
  expiryMode: '0dte',
  underlying: 'spx',
  oiData: 'settled',
  showExposureProfile: false,
  expProfileGex: true,
  expProfileDex: true,
  expProfileVex: true,
  expProfileCex: true,
  exposureProfileMode: 'dealer',
  showExpProfileHeatmap: false,
  showExpProfileT1: false,
  expProfileAnchorFrac: null,    // drag-to-place open-session profile axis (null = left-edge pin)
  expProfileT1AnchorFrac: null,  // drag-to-place T+1 profile axis (null = right-edge pin)
  hedgeDemandAnchorFrac: null,   // drag-to-place Hedging Demand axis (null = pin right of the exposure axis)
  oiProfileAnchorFrac: null,     // drag-to-place OI distribution axis (null = left-edge pin)
  showOiDistribution: false,
  oiDistCallOi: true,
  oiDistPutOi: true,
  oiDistNet: true,
  showOptionsVolume: false,
  optVolCalls: true,
  optVolPuts: true,
  optVolNet: true,
  optVolCumulative: false,       // Child of showOptionsVolume: day-to-date second histogram
  optVolSplit: false,            // Child of showOptionsVolume: at-ask/at-bid paired rows
  optVolFlowNet: false,          // Child of showOptionsVolume: net residual bars (precedence over split)
  optVolFlowPane: false,         // Child of showOptionsVolume: intraday net dealer flow pane
  optVolAnchorFrac: null,        // drag-to-place Options Volume axis (null = offset default pin)
  optVolCumAnchorFrac: null,     // drag-to-place cumulative volume axis (null = offset default pin)
  showUnderlyingCompare: false,      // Underlying Compare strip: right-docked ES/SPX/SPY per-strike lanes
  underlyingCompareMode: 'oi',       // Compare strip fill: 'oi' | 'exposure' | 'volume'
  underlyingCompareGreek: 'gex',     // Compare strip exposure-mode greek
  underlyingCompareZeroLevels: false, // Compare strip: per-lane zero-crossing ticks
  underlyingCompareGammaLevels: false, // Compare strip: per-lane GVWAP + walls
  showForwardOi: false,
  zeroLevels: false,
  zeroLevelsGex: true,
  zeroLevelsDex: true,
  zeroLevelsVex: true,
  zeroLevelsCex: true,
  gammaLevels: false,
  gammaLevelsGvwap: true,
  gammaLevelsBand: true,
  gammaLevelsWalls: true,
  showProjectedCurves: false,
  projGex: true,
  projDex: true,
  projVex: true,
  projCex: true,
  expectedMove: false,
  priorLevels: false,        // Parent toggle for prior day/week/month
  prevDayLevels: true,       // Child of priorLevels (gated by parent, so default-on)
  priorWeekLevels: true,     // Child of priorLevels
  priorMonthLevels: true,    // Child of priorLevels
  overnightLevel: false,
  asiaLevels: false,        // Child of overnightLevel (gated by parent)
  londonLevels: false,      // Child of overnightLevel (gated by parent)
  openingRange: false,
  initialBalance: false,
  openingPrints: false,
  pivotPoints: false,
  sessionMarkers: false,
  vwap: false,
  vwapBand1: false,
  vwapBand2: false,
  vwapSessionMode: 'full',
  volumeProfile: false,
  nakedPOC: false,
  nakedTpoPOC: false,
  hvnZones: false,
  vpSessionMode: 'full',
  deltaProfile: false,
  tpoProfile: false,
  tpoRenderMode: 'histogram',
  tpoSessionScope: 'full',
  tpoDisplayStyle: 'basic',
  cvd: false,
  exposure: false,           // Exposure pane (sub-toggle of showExposureProfile)
  exposureMode: 'dealer',    // inert: the pane follows exposureProfileMode since the 2026-08-10 nesting
  iv: false,
  ivStdDev: false,
  ...OVERLAY_TOGGLE_DEFAULTS,  // indexOverlays parent + spx/spy/nq/rty/ym/zn children (registry SSOT)
  orderflowAbsorption: false,
  orderflowAggressiveOrders: false,
  orderflowContextProfile: false,  // Prior-session profile fills + POC/value/core lines
  orderflowStructure: false,       // Market structure: tier-1/2 pivots + BOS/CHoCH breaks + bias
  orderflowStructureDetail: false, // Child of orderflowStructure: tier-1 swing dots (PPAS default: hidden)
  orderflowRotations: false,       // Parent group: renko rotation overlay (legs + reach)
  orderflowRotationLegs: true,     // Sub: rotation legs zig-zag (gated by orderflowRotations)
  orderflowRotationReach: true,    // Sub: p50/p70/p90 reach levels (gated by orderflowRotations)
  orderflowRotationBrickSize: 8,   // Served renko size for the overlay (8 | 4); picker under Rotations
  hedgingDemand: false,
  showHedgeDemandHeatmap: false,
  hedgeDemandRealized: false,
  showIvSkew: false,
  ivSkewAnchorFrac: null,       // drag-to-place IV Skew axis (null = offset default pin)
  ivSkewBand: true,             // IV Skew sub-layer: default on, gated by parent
  ivSkewShape: false,             // Shape History pane (IV Skew sub-toggle, opt-in)
  ivRealized: false,             // IV Index realized-vol companion (opt-in like ivStdDev)
  tooltipHidden: false,
  tooltipPinned: false,
};

function App() {
  // Auth
  const { user, logout } = useAuth();

  // Admin panel
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  // Docs panel
  const [showDocsPanel, setShowDocsPanel] = useState(false);

  // State
  const [candles, setCandles] = useState([]);
  const [date, setDate] = useState(getLatestSessionDate());
  const [timeframe, setTimeframe] = useState(() => {
    try { const v = localStorage.getItem('timeframe'); if (v) return JSON.parse(v); } catch (e) { /* ignore */ }
    return '5m';
  });
  const [timezone, setTimezone] = useState(() => {
    try { const v = localStorage.getItem('timezone'); if (v) return JSON.parse(v); } catch (e) { /* ignore */ }
    return 'ET';
  });
  const [loading, setLoading] = useState(false);

  const [liveConnected, setLiveConnected] = useState(false);

  // Candlestick color settings (persisted to localStorage)
  const [candleColors, setCandleColors] = useState(() => {
    try {
      const saved = localStorage.getItem('candleColors');
      // Merge over defaults so saves from before the opacity field get opacity: 1
      if (saved) return { upColor: '#22c55e', downColor: '#ef4444', opacity: 1, ...JSON.parse(saved) };
    } catch (e) { /* ignore */ }
    return { upColor: '#22c55e', downColor: '#ef4444', opacity: 1 };
  });
  const handleCandleColorsChange = useCallback((colors) => {
    setCandleColors(colors);
    try { localStorage.setItem('candleColors', JSON.stringify(colors)); } catch (e) { /* ignore */ }
  }, []);

  // Display type: 'candle' or 'line'
  const [displayType, setDisplayType] = useState(() => {
    try { const v = localStorage.getItem('displayType'); if (v) return JSON.parse(v); } catch (e) { /* ignore */ }
    return 'candle';
  });
  const handleDisplayTypeChange = useCallback((type) => {
    setDisplayType(type);
    try { localStorage.setItem('displayType', JSON.stringify(type)); } catch (e) { /* ignore */ }
  }, []);

  // Hide chart grid lines (global, persisted to localStorage)
  const [hideGridLines, setHideGridLines] = useState(() => {
    try { const v = localStorage.getItem('hideGridLines'); if (v) return JSON.parse(v) === true; } catch (e) { /* ignore */ }
    return false;
  });
  const handleHideGridLinesChange = useCallback((v) => {
    setHideGridLines(v);
    try { localStorage.setItem('hideGridLines', JSON.stringify(v)); } catch (e) { /* ignore */ }
  }, []);
  // Reserve chart space for the indicator panel so the price axis is not covered
  const [panelOpen, setPanelOpen] = useState(false);
  const handleClearSettings = useCallback(() => {
    try {
      localStorage.removeItem('timeframe');
      localStorage.removeItem('timezone');
      localStorage.removeItem('indicators');
      localStorage.removeItem('candleColors');
      localStorage.removeItem('displayType');
      localStorage.removeItem('hideGridLines');
      localStorage.removeItem('underlyingCompareWidth');
    } catch (e) { /* ignore */ }
    setTimeframe('5m');
    setTimezone('ET');
    setIndicators(INDICATOR_DEFAULTS);
    setCandleColors({ upColor: '#22c55e', downColor: '#ef4444', opacity: 1 });
    setDisplayType('candle');
    setHideGridLines(false);
  }, []);

  // Parse chart type to determine if volume-based
  const chartTypeInfo = useMemo(() => {
    const chartType = CONFIG.chartTypes.find(ct => ct.value === timeframe);
    if (chartType && chartType.type === 'volume') {
      return {
        isVolumeChart: true,
        volumeSize: chartType.volumeSize,
        timeframe: null,
      };
    }
    return {
      isVolumeChart: false,
      volumeSize: null,
      timeframe: timeframe,
    };
  }, [timeframe]);

  // Indicator toggle states (persisted to localStorage)
  const [indicators, setIndicators] = useState(() => {
    try {
      const saved = localStorage.getItem('indicators');
      if (saved) {
        const parsed = JSON.parse(saved);
        return { ...INDICATOR_DEFAULTS, ...parsed };
      }
    } catch (e) { /* ignore */ }
    return INDICATOR_DEFAULTS;
  });

  // Quote-overlay prop for the chart, derived from the registry toggles.
  // overlayStateKey collapses the scalar toggle values into one dep so the
  // memo stays registry-driven (the indicators object identity changes
  // every update and must not be the dep).
  const overlayStateKey = OVERLAY_STATE_IDS.map((id) => (indicators[id] ? '1' : '0')).join('');
  const quoteOverlays = useMemo(
    () => buildQuoteOverlays(indicators),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [overlayStateKey]);

  // OI Baseline loading state (separate from general loading)
  const [baselineLoading, setBaselineLoading] = useState(false);

  // GEX plugin loading state (for disabling toggles during async enable)
  const [greeksLoading, setGreeksLoading] = useState(false);


  // Server status (ES + GEX): one poller for the CacheStatusBar family
  // dicts AND the refresh-in-progress flag
  const [cacheStatus, setCacheStatus] = useState(null);
  const [greeksRefreshing, setGreeksRefreshing] = useState(false);
  // Per-horizon refit health of the live-curve rows (side-panel state line)
  const [liveCurvesHealth, setLiveCurvesHealth] = useState(null);
  const wasRefreshingRef = useRef(false);

  // Chart refs for hook
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const updateCandleRef = useRef(null);

  // Session level indicators hook - fetch for all days in chart range
  // getLatestSessionDate() returns next day after 6 PM ET, naturally including
  // next-session preview levels during overnight
  // Only fetch when at least one session level indicator is enabled
  const anyPriorLevelEnabled = indicators.priorLevels &&
    (indicators.prevDayLevels || indicators.priorWeekLevels || indicators.priorMonthLevels);
  const anySessionLevelEnabled = anyPriorLevelEnabled ||
    (indicators.overnightLevel && (indicators.asiaLevels || indicators.londonLevels)) ||
    indicators.openingRange || indicators.initialBalance ||
    indicators.openingPrints || indicators.pivotPoints;
  const anyZeroLevelEnabled = indicators.zeroLevels;
  const emEnabled = indicators.expectedMove;
  const anyGammaLevelEnabled = indicators.gammaLevels;

  const levelIndicators = useLevelIndicators(date, {
    endDate: getLatestSessionDate(),
    expiryMode: indicators.expiryMode,
    underlying: indicators.underlying,
    enabled: anySessionLevelEnabled || anyZeroLevelEnabled || emEnabled || anyGammaLevelEnabled,
    fetchSession: anySessionLevelEnabled,
    fetchZero: anyZeroLevelEnabled,
    fetchEm: emEnabled,
    fetchGamma: anyGammaLevelEnabled,
  });

  // Handle live candle updates
  const handleLiveCandleUpdate = useCallback((candle, isComplete) => {
    if (updateCandleRef.current) {
      updateCandleRef.current(candle, isComplete);
    }
  }, []);

  // Handle bulk candles from WebSocket
  const handleBulkCandles = useCallback((bulkCandles) => {
    if (Array.isArray(bulkCandles) && bulkCandles.length > 0) {
      console.log('[App] Received bulk candles:', bulkCandles.length);
      setCandles(prev => {
        // Merge with existing candles, updating duplicates
        const candleMap = new Map(prev.map(c => [c.time, c]));
        bulkCandles.forEach(c => candleMap.set(c.time, c));
        return Array.from(candleMap.values()).sort((a, b) => a.time - b.time);
      });
    }
  }, []);

  // Live candles hook for real-time updates (time-based charts only)
  const liveCandles = useLiveCandles(
    handleLiveCandleUpdate,
    handleBulkCandles,
    chartTypeInfo.timeframe || '5m',
    !chartTypeInfo.isVolumeChart
  );

  // Volume candle refs
  const volumeChartRef = useRef(null);
  const volumeUpdateCandleRef = useRef(null);

  // Memoized callback for volume chart ready
  const handleVolumeChartReady = useCallback((chart, series, container, updateCandle) => {
    volumeChartRef.current = chart;
    volumeUpdateCandleRef.current = updateCandle;
    console.log('[App] Volume chart ready');
  }, []);

  // Handle volume candle updates
  const handleVolumeCandelUpdate = useCallback((candle, isComplete) => {
    if (volumeUpdateCandleRef.current) {
      volumeUpdateCandleRef.current(candle, isComplete);
    }
  }, []);

  // Handle bulk volume candles
  // Volume candles are handled directly by ESChart when isVolumeChart=true.
  // Callback must exist - useLiveVolumeCandles gates bulkReceived and connected state on it.
  const handleBulkVolumeCandles = useCallback(() => {}, []);

  // Live volume candles hook (volume-based charts only)
  const liveVolumeCandles = useLiveVolumeCandles(
    handleVolumeCandelUpdate,
    handleBulkVolumeCandles,
    chartTypeInfo.volumeSize || 5000,
    chartTypeInfo.isVolumeChart
  );

  // Update live connection status
  useEffect(() => {
    if (chartTypeInfo.isVolumeChart) {
      setLiveConnected(liveVolumeCandles.connected);
    } else {
      setLiveConnected(liveCandles.connected);
    }
  }, [liveCandles.connected, liveVolumeCandles.connected, chartTypeInfo.isVolumeChart]);

  // Fetch candle data from the backend API (time-based charts only)
  const fetchCandles = useCallback(async () => {
    // Skip for volume-based charts - they fetch their own data
    if (chartTypeInfo.isVolumeChart) {
      return;
    }

    setLoading(true);

    // Helper to get previous weekday date string
    const getPreviousWeekday = (dateStr) => {
      const d = new Date(dateStr + 'T12:00:00');
      d.setDate(d.getDate() - 1);
      // Skip weekends
      while (d.getDay() === 0 || d.getDay() === 6) {
        d.setDate(d.getDate() - 1);
      }
      return d.toISOString().split('T')[0];
    };

    try {
      // Use the backend API for ES candles (from selected date to most recent trading day)
      let endDate = getLatestSessionDate();
      let startDate = date;
      let url = `${CONFIG.apiUrl}/api/es/candles?start_date=${startDate}&timeframe=${chartTypeInfo.timeframe}&end_date=${endDate}`;
      let attempts = 0;
      const maxAttempts = 5; // Try up to 5 previous days (to handle holidays)

      console.log('[App] Fetching candles:', url);

      let response = await authFetch(url);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      let data = await response.json();

      // If no candles returned, try previous trading days (handles holidays)
      while ((!data.candles || data.candles.length === 0) && attempts < maxAttempts) {
        attempts++;
        startDate = getPreviousWeekday(startDate);
        endDate = startDate;
        url = `${CONFIG.apiUrl}/api/es/candles?start_date=${startDate}&timeframe=${timeframe}&end_date=${endDate}`;
        console.log(`[App] No data for requested date, trying previous day: ${startDate}`);

        response = await authFetch(url);
        if (response.ok) {
          data = await response.json();
        }
      }

      if (data.candles && data.candles.length > 0) {
        setCandles(data.candles);
        // Update the date state if we fell back to a previous day
        if (startDate !== date) {
          console.log(`[App] Holiday detected, falling back to ${startDate}`);
          setDate(startDate);
        }
        console.log('[App] Loaded', data.candles.length, 'candles from', startDate, 'to', endDate);
      } else {
        console.warn('[App] No candles returned after', maxAttempts, 'attempts');
        setCandles([]);
      }
    } catch (err) {
      console.error('[App] Error fetching candles:', err);
      setCandles([]);
    } finally {
      setLoading(false);
    }
  }, [date, chartTypeInfo.timeframe, chartTypeInfo.isVolumeChart]);

  // Track if this is the initial mount (to skip auto-fetch on page load)
  const isInitialMount = useRef(true);

  // Fetch candles when date/timeframe changes (but NOT on initial mount)
  // On initial mount, data comes from WebSocket bulk_candles
  // User can also trigger refresh via the refresh button
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      // Fallback: if WebSocket doesn't deliver bulk candles within 3s
      // (e.g. non-trading day, SSE down), fetch via REST
      const fallbackTimer = setTimeout(() => {
        if (!candles || candles.length === 0) {
          console.log('[App] No bulk candles received after 3s, fetching via REST');
          fetchCandles();
        }
      }, 3000);
      return () => clearTimeout(fallbackTimer);
    }
    // Fetch when date or timeframe changes after initial load
    fetchCandles();
  }, [date, timeframe]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle chart ready callback
  const handleChartReady = useCallback((chart, series, container, updateCandle) => {
    chartRef.current = chart;
    seriesRef.current = series;
    updateCandleRef.current = updateCandle;
    console.log('[App] Chart ready, live updates enabled');
  }, []);

  // Handle indicator toggle from NavigationBar
  const handleIndicatorToggle = useCallback((indicatorId, enabled) => {
    setIndicators(prev => {
      const next = { ...prev, [indicatorId]: enabled };
      try { localStorage.setItem('indicators', JSON.stringify(next)); } catch (e) { /* ignore */ }
      return next;
    });
  }, []);

  // Bulk apply a saved preset snapshot from the side panel (one atomic merge so
  // the chart effects see a single indicators change)
  const handleApplyIndicators = useCallback((snapshot) => {
    setIndicators(prev => {
      const next = { ...prev, ...snapshot };
      try { localStorage.setItem('indicators', JSON.stringify(next)); } catch (e) { /* ignore */ }
      return next;
    });
  }, []);

  // Handle timeframe/chart type change
  const handleTimeframeChange = useCallback((newTimeframe) => {
    setTimeframe(newTimeframe);
    try { localStorage.setItem('timeframe', JSON.stringify(newTimeframe)); } catch (e) { /* ignore */ }
  }, []);

  // Fetch server status periodically: the single status poller. Feeds the
  // CacheStatusBar (es/greeks family dicts, via prop) and the refresh flag.
  const fetchServerStatus = useCallback(async () => {
    try {
      const response = await authFetch(`${CONFIG.apiUrl}/api/cache/status`);
      if (response.ok) {
        const data = await response.json();
        setCacheStatus(data);
        setGreeksRefreshing(Boolean(data.refresh_running));
      }
    } catch (err) {
      console.warn('[App] Could not fetch server status:', err);
    }
  }, []);

  // Poll status - 2s while a heal or refresh is active, 30s idle
  const serverHealing = cacheStatus?.es?.status === 'healing' || cacheStatus?.greeks?.status === 'healing';
  useEffect(() => {
    fetchServerStatus();
    const pollInterval = (serverHealing || greeksRefreshing) ? 2000 : 30000;
    const interval = setInterval(fetchServerStatus, pollInterval);
    return () => clearInterval(interval);
  }, [fetchServerStatus, serverHealing, greeksRefreshing]);

  // Detect when refresh completes and trigger chart update (debounced)
  const refreshCompleteTimer = useRef(null);
  useEffect(() => {
    // Check if we just finished refreshing
    if (wasRefreshingRef.current && !greeksRefreshing) {
      // Debounce: if status bounces (true→false→true→false), only fire once
      if (refreshCompleteTimer.current) {
        clearTimeout(refreshCompleteTimer.current);
      }
      refreshCompleteTimer.current = setTimeout(() => {
        refreshCompleteTimer.current = null;
        console.log('[App] Data refresh completed, triggering chart updates');
        // Re-fetch ES candles
        fetchCandles();
      }, 3000);
    }
    wasRefreshingRef.current = greeksRefreshing;
    return () => {
      if (refreshCompleteTimer.current) {
        clearTimeout(refreshCompleteTimer.current);
      }
    };
  }, [greeksRefreshing, fetchCandles]);

  return (
    <div style={{
      width: '100vw',
      height: '100vh',
      overflow: 'hidden',
      backgroundColor: '#0a0e1a',
      color: '#e0e0e0',
      display: 'flex',
      flexDirection: 'column',
      padding: '16px',
      boxSizing: 'border-box',
    }}>
      {/* Cache Status Bar - shows during data loading (status fed by the
          single App-level poller) */}
      <CacheStatusBar
        status={cacheStatus}
        loading={loading}
        loadingMessage={`Loading ${timeframe} candles from ${date}...`}
      />

      {/* Navigation Bar */}
      <NavigationBar
        date={date}
        timeframe={timeframe}
        timezone={timezone}
        indicators={indicators}
        connected={liveConnected}
        onDateChange={setDate}
        onTimeframeChange={handleTimeframeChange}
        onTimezoneChange={(tz) => { setTimezone(tz); try { localStorage.setItem('timezone', JSON.stringify(tz)); } catch (e) { /* ignore */ } }}
        onIndicatorToggle={handleIndicatorToggle}
        userRole={user?.role}
        onDocsToggle={() => { setShowDocsPanel(prev => !prev); setShowAdminPanel(false); }}
        onAdminToggle={() => { setShowAdminPanel(prev => !prev); setShowDocsPanel(false); }}
        username={user?.username}
        onLogout={logout}
        candleColors={candleColors}
        onCandleColorsChange={handleCandleColorsChange}
        displayType={displayType}
        onDisplayTypeChange={handleDisplayTypeChange}
        hideGridLines={hideGridLines}
        onHideGridLinesChange={handleHideGridLinesChange}
        onClearSettings={handleClearSettings}
      />

      {/* Admin Panel */}
      <AdminPanel
        visible={showAdminPanel}
        onClose={() => setShowAdminPanel(false)}
      />

      {/* Documentation Panel */}
      <DocsPanel
        visible={showDocsPanel}
        onClose={() => setShowDocsPanel(false)}
      />

      {/* Chart Container */}
      <div style={{
        flex: 1,
        borderRadius: '10px',
        overflow: 'hidden',
        border: '1px solid #2a3344',
        background: '#1a1f2e',
        position: 'relative',
        marginRight: panelOpen ? 300 : 0,
        transition: 'margin-right 0.28s cubic-bezier(0.4, 0, 0.2, 1)',
      }}>
        {/* Single chart component handles both time-based and volume-based charts.
            key={displayType}: candle/line switch remounts the chart so all series-attached
            plugins rebuild against the fresh series (in-place series swap orphans them) */}
        <ESChart
          key={displayType}
          candles={candles}
          date={date}
          endDate={getLatestSessionDate()}
          timeframe={chartTypeInfo.timeframe || '5m'}
          // Volume chart mode props
          isVolumeChart={chartTypeInfo.isVolumeChart}
          volumeSize={chartTypeInfo.volumeSize || 5000}
          onVolumeChartReady={handleVolumeChartReady}
          // Indicator props (work for both chart types)
          timezone={timezone}
          expiryMode={indicators.expiryMode}
          underlying={indicators.underlying}
          showOiDistribution={indicators.showOiDistribution}
          oiDistToggles={{ callOi: indicators.oiDistCallOi ?? true, putOi: indicators.oiDistPutOi ?? true, net: indicators.oiDistNet ?? true }}
          showOptionsVolume={indicators.showOptionsVolume}
          optVolToggles={{ callVol: indicators.optVolCalls ?? true, putVol: indicators.optVolPuts ?? true, net: indicators.optVolNet ?? true }}
          optVolCumulative={indicators.showOptionsVolume && indicators.optVolCumulative}
          optVolSplit={indicators.showOptionsVolume && indicators.optVolSplit}
          optVolFlowNet={indicators.showOptionsVolume && indicators.optVolSplit && indicators.optVolFlowNet}
          showVolFlowPane={indicators.showOptionsVolume && indicators.optVolFlowPane}
          showUnderlyingCompare={indicators.showUnderlyingCompare}
          underlyingCompareMode={indicators.underlyingCompareMode || 'oi'}
          underlyingCompareGreek={indicators.underlyingCompareGreek || 'gex'}
          underlyingCompareZeroLevels={indicators.underlyingCompareZeroLevels}
          underlyingCompareGammaLevels={indicators.underlyingCompareGammaLevels}
          optVolAnchorFrac={indicators.optVolAnchorFrac ?? null}
          optVolCumAnchorFrac={indicators.optVolCumAnchorFrac ?? null}
          showForwardOi={indicators.showForwardOi}
          baselineLoading={baselineLoading}
          onBaselineLoadingChange={setBaselineLoading}
          onGreeksLoadingChange={setGreeksLoading}
          showPrevDayLevels={indicators.priorLevels && indicators.prevDayLevels}
          showPriorWeekLevels={indicators.priorLevels && indicators.priorWeekLevels}
          showPriorMonthLevels={indicators.priorLevels && indicators.priorMonthLevels}
          showAsiaLevels={indicators.overnightLevel && indicators.asiaLevels}
          showLondonLevels={indicators.overnightLevel && indicators.londonLevels}
          showOpeningRange={indicators.openingRange}
          showInitialBalance={indicators.initialBalance}
          showOpeningPrints={indicators.openingPrints}
          showPivotPoints={indicators.pivotPoints}
          showZeroLevels={indicators.zeroLevels}
          zeroLevelGreeks={{ gex: indicators.zeroLevelsGex ?? true, dex: indicators.zeroLevelsDex ?? true, vex: indicators.zeroLevelsVex ?? true, cex: indicators.zeroLevelsCex ?? true }}
          showGammaLevels={indicators.gammaLevels}
          gammaLevelToggles={{ gvwap: indicators.gammaLevelsGvwap ?? true, band: (indicators.gammaLevelsGvwap ?? true) && (indicators.gammaLevelsBand ?? true), walls: indicators.gammaLevelsWalls ?? true }}
          showZeroLevelsLive={indicators.oiData === 'live'}
          showExpectedMove={indicators.expectedMove}
          showSessionMarkers={indicators.sessionMarkers}
          showVWAP={indicators.vwap}
          showVWAPBand1={indicators.vwapBand1}
          showVWAPBand2={indicators.vwapBand2}
          vwapSessionMode={indicators.vwapSessionMode || 'full'}
          showVolumeProfile={indicators.volumeProfile}
          showNakedPOC={indicators.nakedPOC}
          showNakedTpoPOC={indicators.nakedTpoPOC}
          showHVNZones={indicators.hvnZones}
          vpSessionMode={indicators.vpSessionMode}
          showDeltaProfile={indicators.deltaProfile}
          showTPOProfile={indicators.tpoProfile}
          tpoRenderMode={indicators.tpoRenderMode}
          tpoSessionScope={indicators.tpoSessionScope || 'full'}
          tpoDisplayStyle={indicators.tpoDisplayStyle || 'basic'}
          showCVD={indicators.cvd}
          showExposure={indicators.showExposureProfile && indicators.exposure}
          exposureLiveSmile={indicators.oiData === 'live'}
          onLiveCurvesHealth={setLiveCurvesHealth}
          exposureMode={indicators.exposureProfileMode || 'dealer'}
          showProjectedCurves={indicators.showProjectedCurves}
          projectedCurveGreeks={{ gex: indicators.projGex, dex: indicators.projDex, vex: indicators.projVex, cex: indicators.projCex }}
          projCurvesLive={indicators.oiData === 'live'}
          showExposureProfile={indicators.showExposureProfile}
          expProfileLiveSmile={indicators.oiData === 'live'}
          expProfileToggles={{ gex: indicators.expProfileGex ?? true, dex: indicators.expProfileDex ?? true, vex: indicators.expProfileVex ?? true, cex: indicators.expProfileCex ?? true }}
          exposureProfileMode={indicators.exposureProfileMode || 'dealer'}
          showExpProfileHeatmap={indicators.showExpProfileHeatmap}
          showExpProfileT1={indicators.showExpProfileT1}
          expProfileAnchorFrac={indicators.expProfileAnchorFrac ?? null}
          expProfileT1AnchorFrac={indicators.expProfileT1AnchorFrac ?? null}
          hedgeDemandAnchorFrac={indicators.hedgeDemandAnchorFrac ?? null}
          oiProfileAnchorFrac={indicators.oiProfileAnchorFrac ?? null}
          onProfileAnchorChange={handleIndicatorToggle}
          showIV={indicators.iv}
          showIVStdDev={indicators.ivStdDev}
          quoteOverlays={quoteOverlays}
          showOrderflowAbsorption={indicators.orderflowAbsorption}
          showOrderflowAggressiveOrders={indicators.orderflowAggressiveOrders}
          showOrderflowContextProfile={indicators.priorLevels && indicators.orderflowContextProfile}
          showOrderflowStructure={indicators.orderflowStructure}
          showOrderflowStructureDetail={indicators.orderflowStructure && indicators.orderflowStructureDetail}
          showOrderflowRotationLegs={indicators.orderflowRotations && indicators.orderflowRotationLegs}
          showOrderflowRotationReach={indicators.orderflowRotations && indicators.orderflowRotationReach}
          rotationBrickSize={indicators.orderflowRotationBrickSize}
          showHedgingDemand={indicators.hedgingDemand}
          showHedgeDemandHeatmap={indicators.showHedgeDemandHeatmap}
          hedgeDemandRealized={indicators.hedgeDemandRealized}
          showIvSkew={indicators.showIvSkew}
          ivSkewAnchorFrac={indicators.ivSkewAnchorFrac ?? null}
          ivSkewBand={indicators.showIvSkew && indicators.ivSkewBand !== false}
          showIvSkewShape={indicators.showIvSkew && indicators.ivSkewShape === true}
          showIVRealized={indicators.iv && indicators.ivRealized === true}
          tooltipHidden={indicators.tooltipHidden}
          tooltipPinned={indicators.tooltipPinned}
          levelData={levelIndicators}
          onChartReady={handleChartReady}
          candleColors={candleColors}
          displayType={displayType}
          hideGridLines={hideGridLines}
        />
      </div>

      {/* Indicator Side Panel */}
      <IndicatorSidePanel
        indicators={indicators}
        onIndicatorToggle={handleIndicatorToggle}
        onApplyIndicators={handleApplyIndicators}
        greeksLoading={greeksLoading}
        onOpenChange={setPanelOpen}
        liveCurvesHealth={liveCurvesHealth}
      />
    </div>
  );
}

export default App;
